import { subMonths } from "date-fns";

import { DATE_FORMATS_FNS, statusFilters } from "components";
import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { PIN_TYPES } from "models";
import { DEFAULT_PIN_TYPES } from "modules/WarrantyDashboard/components/WarrantyDashboardTable/hooks";
import { MECHANIC_FIXED, ORDER_BY, WARRANTY_TIME_PERIODS, useGetMechanicFixedOptions } from "modules/WarrantyDashboard/hooks";
import { setWarrantyDashboardFilters, useGetWarrantyDashboardFilters } from "modules/WarrantyDashboard/hooks/useGetWarrantyDashboardFilters";
import { WARRANTY_STORAGE_KEYS } from "modules/WarrantyDashboard/storageKeys";
import { FAKE_STATUSES } from "utils/appointmentUtils";
import { setPreference } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { DealersLocationsDropdownValue, StatusFilter } from "components";

export const useWarrantyDashboardFilterHandlers = () => {
  const { filters } = useGetWarrantyDashboardFilters();
  const { mechanic_fixed, pin_type_id } = filters;
  const { selectedLocation } = useSelectedLocation();
  const mechanicFixedOptions = useGetMechanicFixedOptions();

  const selectedMechanicFixed =
    mechanic_fixed !== null ? mechanicFixedOptions.find(element => element.value === (mechanic_fixed ? MECHANIC_FIXED.Yes : MECHANIC_FIXED.No))?.value : undefined;
  const isOnlyRemarksSelected = pin_type_id?.every(pinType => pinType === PIN_TYPES.Remarks);

  const appointmentStatusIdentifiers = statusFilters.filter(status => !FAKE_STATUSES.includes(status.identifier));

  const handleResetFilters = () => {
    const defaultDateFrom = formatDate(subMonths(new Date(), 1), DATE_FORMATS_FNS.ISOFormatEndOfDay);
    const defaultDateTo = formatDate(new Date(), DATE_FORMATS_FNS.ISOFormatEndOfDay);

    setPreference(WARRANTY_STORAGE_KEYS.AppointmentStatusIdentifiersPreference, appointmentStatusIdentifiers);
    setPreference(WARRANTY_STORAGE_KEYS.BrandsPreference, null);
    setPreference(WARRANTY_STORAGE_KEYS.ClaimedPreference, false);
    setPreference(WARRANTY_STORAGE_KEYS.DeletedPreference, false);
    setPreference(WARRANTY_STORAGE_KEYS.DealerPreference, []);
    setPreference(WARRANTY_STORAGE_KEYS.LocationPreference, selectedLocation ? [selectedLocation.id] : []);
    setPreference(WARRANTY_STORAGE_KEYS.MechanicFixedPreference, null);
    setPreference(WARRANTY_STORAGE_KEYS.PinStatusPreference, null);
    setPreference(WARRANTY_STORAGE_KEYS.PinTypePreference, DEFAULT_PIN_TYPES);
    setPreference(WARRANTY_STORAGE_KEYS.QuestionStatusPreference, null);
    setPreference(WARRANTY_STORAGE_KEYS.WarrantyTypePreference, null);
    setPreference(WARRANTY_STORAGE_KEYS.ClaimSortPreference, null);
    setPreference(WARRANTY_STORAGE_KEYS.TimePeriodPreference, WARRANTY_TIME_PERIODS.LastMonth);
    setPreference(WARRANTY_STORAGE_KEYS.OrderByPreference, ORDER_BY.AppointmentDateAscending);
    setPreference(WARRANTY_STORAGE_KEYS.PinDateFromPreference, defaultDateFrom);
    setPreference(WARRANTY_STORAGE_KEYS.PinDateToPreference, defaultDateTo);
    setPreference(WARRANTY_STORAGE_KEYS.AppointmentDateFromPreference, defaultDateFrom);
    setPreference(WARRANTY_STORAGE_KEYS.AppointmentDateToPreference, defaultDateTo);
    setPreference(WARRANTY_STORAGE_KEYS.AppointmentTimePeriodPreference, WARRANTY_TIME_PERIODS.LastMonth);

    setWarrantyDashboardFilters({
      ...filters,
      appointment_statuses: appointmentStatusIdentifiers,
      brands: null,
      claimed: false,
      deleted: false,
      pin_date_from: defaultDateFrom,
      pin_date_to: defaultDateTo,
      appointment_date_from: defaultDateFrom,
      appointment_date_to: defaultDateTo,
      dealer_ids: [],
      location_ids: selectedLocation ? [selectedLocation.id] : [],
      mechanic_fixed: null,
      order_by: ORDER_BY.AppointmentDateAscending,
      page: 1,
      pin_status_id: null,
      pin_type_id: DEFAULT_PIN_TYPES,
      search_term: "",
      status: null,
      warranty_type_id: null,
      claim_order_by: null,
      time_period: WARRANTY_TIME_PERIODS.LastMonth,
      appointment_time_period: WARRANTY_TIME_PERIODS.LastMonth,
      pin_status_refresh_timestamp: formatDate(new Date(), DATE_FORMATS_FNS.ISOFormat)
    });
  };

  const handleTimePeriodChange = (timePeriod: WARRANTY_TIME_PERIODS) => {
    if (timePeriod === WARRANTY_TIME_PERIODS.CustomTimePeriod) {
      setPreference(WARRANTY_STORAGE_KEYS.TimePeriodPreference, timePeriod);
      return setWarrantyDashboardFilters({ ...filters, time_period: timePeriod, page: 1 });
    }

    const pin_date_from = formatDate(subMonths(new Date(), timePeriod), DATE_FORMATS_FNS.ISOFormatEndOfDay);
    const pin_date_to = formatDate(new Date(), DATE_FORMATS_FNS.ISOFormatEndOfDay);

    setPreference(WARRANTY_STORAGE_KEYS.TimePeriodPreference, timePeriod);
    setPreference(WARRANTY_STORAGE_KEYS.PinDateFromPreference, pin_date_from);
    setPreference(WARRANTY_STORAGE_KEYS.PinDateToPreference, pin_date_to);
    setWarrantyDashboardFilters({ ...filters, pin_date_from, pin_date_to, time_period: timePeriod, page: 1 });
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    const pin_date_from = start ? formatDate(start, DATE_FORMATS_FNS.ISOFormatEndOfDay) : null;
    const pin_date_to = end ? formatDate(end, DATE_FORMATS_FNS.ISOFormatEndOfDay) : null;
    setPreference(WARRANTY_STORAGE_KEYS.PinDateFromPreference, pin_date_from);
    setPreference(WARRANTY_STORAGE_KEYS.PinDateToPreference, pin_date_to);
    setWarrantyDashboardFilters({ ...filters, pin_date_from, pin_date_to, page: 1 });
  };

  const handleAppointmentTimePeriodChange = (timePeriod: WARRANTY_TIME_PERIODS) => {
    if (timePeriod === WARRANTY_TIME_PERIODS.CustomTimePeriod) {
      setPreference(WARRANTY_STORAGE_KEYS.AppointmentTimePeriodPreference, timePeriod);
      return setWarrantyDashboardFilters({ ...filters, appointment_time_period: timePeriod, page: 1 });
    }

    const appointment_date_from = formatDate(subMonths(new Date(), timePeriod), DATE_FORMATS_FNS.ISOFormatEndOfDay);
    const appointment_date_to = formatDate(new Date(), DATE_FORMATS_FNS.ISOFormatEndOfDay);

    setPreference(WARRANTY_STORAGE_KEYS.AppointmentTimePeriodPreference, timePeriod);
    setPreference(WARRANTY_STORAGE_KEYS.AppointmentDateFromPreference, appointment_date_from);
    setPreference(WARRANTY_STORAGE_KEYS.AppointmentDateToPreference, appointment_date_to);
    setWarrantyDashboardFilters({ ...filters, appointment_date_from, appointment_date_to, appointment_time_period: timePeriod, page: 1 });
  };

  const handleAppointmentDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    const appointment_date_from = start ? formatDate(start, DATE_FORMATS_FNS.ISOFormatEndOfDay) : null;
    const appointment_date_to = end ? formatDate(end, DATE_FORMATS_FNS.ISOFormatEndOfDay) : null;
    setPreference(WARRANTY_STORAGE_KEYS.AppointmentDateFromPreference, appointment_date_from);
    setPreference(WARRANTY_STORAGE_KEYS.AppointmentDateToPreference, appointment_date_to);
    setWarrantyDashboardFilters({ ...filters, appointment_date_from, appointment_date_to, page: 1 });
  };
  const handleSearchChange = (value: string) => setWarrantyDashboardFilters({ ...filters, search_term: value });
  const handleBrandsChange = (brands: string[]) => {
    setPreference(WARRANTY_STORAGE_KEYS.BrandsPreference, brands);
    setWarrantyDashboardFilters({ ...filters, brands, page: 1 });
  };
  const handleClaimedChange = (claimed: boolean) => {
    setPreference(WARRANTY_STORAGE_KEYS.ClaimedPreference, claimed);
    setWarrantyDashboardFilters({ ...filters, claimed, page: 1 });
  };
  const handleDeletedChange = (deleted: boolean) => {
    setPreference(WARRANTY_STORAGE_KEYS.DeletedPreference, deleted);
    if (deleted) setPreference(WARRANTY_STORAGE_KEYS.PinStatusPreference, null);
    setPreference(WARRANTY_STORAGE_KEYS.ClaimedPreference, false);
    setWarrantyDashboardFilters({ ...filters, deleted, ...(deleted && { pin_status_id: null }), claimed: false, page: 1 });
  };
  const handlePinStatusChange = (value: number[]) => {
    setPreference(WARRANTY_STORAGE_KEYS.PinStatusPreference, value.length ? value : null);
    setWarrantyDashboardFilters({ ...filters, pin_status_id: value, pin_status_refresh_timestamp: formatDate(new Date(), DATE_FORMATS_FNS.ISOFormat), page: 1 });
  };
  const handleMechanicFixedChange = (value: number | null) => {
    const mechanicFixed = value ? value === MECHANIC_FIXED.Yes : null;
    setPreference(WARRANTY_STORAGE_KEYS.MechanicFixedPreference, mechanicFixed);
    setWarrantyDashboardFilters({ ...filters, mechanic_fixed: mechanicFixed, page: 1 });
  };
  const handleDealerLocationChange = ({ dealerIds, locationIds }: DealersLocationsDropdownValue) => {
    setPreference(WARRANTY_STORAGE_KEYS.DealerPreference, dealerIds);
    setPreference(WARRANTY_STORAGE_KEYS.LocationPreference, locationIds);
    setWarrantyDashboardFilters({ ...filters, dealer_ids: dealerIds, location_ids: locationIds, page: 1 });
  };
  const handleAppointmentStatusUpdate = (appointmentStatuses: StatusFilter[]) => {
    setPreference(WARRANTY_STORAGE_KEYS.AppointmentStatusIdentifiersPreference, appointmentStatuses);
    setWarrantyDashboardFilters({ ...filters, appointment_statuses: appointmentStatuses, page: 1 });
  };

  return {
    mechanicFixedOptions,
    isOnlyRemarksSelected,
    selectedMechanicFixed,
    handleDateChange,
    handleAppointmentDateChange,
    handleResetFilters,
    handleSearchChange,
    handleBrandsChange,
    handleClaimedChange,
    handleDeletedChange,
    handlePinStatusChange,
    handleTimePeriodChange,
    handleAppointmentTimePeriodChange,
    handleMechanicFixedChange,
    handleDealerLocationChange,
    handleAppointmentStatusUpdate
  };
};
